import { useState, useEffect, useRef } from "react";

// ─── Brand tokens ────────────────────────────────────────────────────────────
const NAVY   = "#1A3B6E";
const MID    = "#2C5FA8";
const LIGHT  = "#E8F2FB";
const KROGER = "#21409A";

// ─── Kroger SVG wordmark (faithful SVG recreation, replace with official asset) ─
function KrogerWordmark({ height = 32, color = "#FFFFFF" }) {
  return (
    <svg height={height} viewBox="0 0 130 42" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Kroger">
      <rect width="130" height="42" rx="4" fill={KROGER}/>
      <text
        x="65" y="29"
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontStyle="italic"
        fontWeight="700"
        fontSize="24"
        letterSpacing="1"
        fill="#FFFFFF"
      >Kroger</text>
    </svg>
  );
}

// ─── School building icon (flat silhouette matching brand asset) ──────────────
function SchoolIcon({ size = 40, color = "#FFFFFF" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="18" width="32" height="18" rx="1" fill={color} opacity="0.95"/>
      <polygon points="20,4 2,20 38,20" fill={color}/>
      <rect x="16" y="26" width="8" height="10" fill={NAVY}/>
      <rect x="8"  y="22" width="5" height="5" fill={NAVY} opacity="0.5"/>
      <rect x="27" y="22" width="5" height="5" fill={NAVY} opacity="0.5"/>
      <rect x="18" y="8" width="4" height="4" fill={NAVY} opacity="0.4"/>
      <rect x="19" y="2" width="2" height="8" fill={color} opacity="0.7"/>
    </svg>
  );
}

// ─── Cart / depot icon ────────────────────────────────────────────────────────
function DepotIcon({ size = 40, color = "#FFFFFF" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="14" width="26" height="16" rx="2" fill={color} opacity="0.9"/>
      <rect x="2" y="10" width="10" height="6" rx="1" fill={color} opacity="0.6"/>
      <path d="M8 30 Q8 35 13 35 Q18 35 18 30" stroke={color} strokeWidth="2" fill="none"/>
      <path d="M22 30 Q22 35 27 35 Q32 35 32 30" stroke={color} strokeWidth="2" fill="none"/>
      <rect x="10" y="18" width="6" height="6" rx="1" fill={NAVY} opacity="0.5"/>
      <rect x="20" y="18" width="6" height="6" rx="1" fill={NAVY} opacity="0.5"/>
    </svg>
  );
}

// ─── Token icon ───────────────────────────────────────────────────────────────
function TokenIcon({ size = 48, color = NAVY }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="22" fill={LIGHT} stroke={color} strokeWidth="2"/>
      <circle cx="24" cy="24" r="14" fill={color} opacity="0.12"/>
      <text x="24" y="30" textAnchor="middle" fontFamily="Georgia,serif" fontWeight="700" fontSize="18" fill={color}>R</text>
      <circle cx="24" cy="24" r="20" fill="none" stroke={color} strokeWidth="1" strokeDasharray="4 3" opacity="0.4"/>
    </svg>
  );
}

// ─── Useanimation on scroll ───────────────────────────────────────────────────
function useFadeIn(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, visible];
}

function FadeIn({ children, delay = 0, className = "", style = {} }) {
  const [ref, visible] = useFadeIn();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
        ...style
      }}
    >
      {children}
    </div>
  );
}

// ─── Step card ────────────────────────────────────────────────────────────────
function StepCard({ number, icon, title, body, delay }) {
  return (
    <FadeIn delay={delay} style={{ flex: "1 1 260px", minWidth: 240 }}>
      <div style={{
        background: "#FFFFFF",
        border: `1.5px solid ${LIGHT}`,
        borderRadius: 16,
        padding: "32px 28px",
        height: "100%",
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden"
      }}>
        <div style={{
          position: "absolute", top: 16, right: 20,
          fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 64,
          color: LIGHT, lineHeight: 1, userSelect: "none", pointerEvents: "none"
        }}>{number}</div>
        <div style={{ marginBottom: 16 }}>{icon}</div>
        <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 20, fontWeight: 700, color: NAVY, marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 15, color: "#4A5568", lineHeight: 1.65 }}>{body}</div>
      </div>
    </FadeIn>
  );
}

// ─── Donate option card ───────────────────────────────────────────────────────
function DonateCard({ icon, title, body, cta, href, delay }) {
  return (
    <FadeIn delay={delay} style={{ flex: "1 1 280px", minWidth: 260 }}>
      <div style={{
        background: "#FFFFFF",
        border: `1.5px solid ${LIGHT}`,
        borderRadius: 16,
        padding: "36px 32px",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 16
      }}>
        <div style={{ width: 52, height: 52, borderRadius: 12, background: LIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {icon}
        </div>
        <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 22, fontWeight: 700, color: NAVY }}>{title}</div>
        <div style={{ fontSize: 15, color: "#4A5568", lineHeight: 1.7, flex: 1 }}>{body}</div>
        {cta && (
          <a href={href || "#"} style={{
            display: "inline-block",
            background: NAVY,
            color: "#FFFFFF",
            padding: "12px 24px",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            textDecoration: "none",
            textAlign: "center",
            transition: "background 0.2s"
          }}
          onMouseEnter={e => e.currentTarget.style.background = MID}
          onMouseLeave={e => e.currentTarget.style.background = NAVY}
          >{cta}</a>
        )}
      </div>
    </FadeIn>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* ── Google Font load ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', system-ui, sans-serif; background: #F7FAFD; }
        html { scroll-behavior: smooth; }
        ::selection { background: ${LIGHT}; color: ${NAVY}; }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes fadeDown { from { opacity:0; transform: translateY(-12px); } to { opacity:1; transform: translateY(0); } }
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        .desktop-nav { display: flex; }
        .hamburger-btn { display: none; }
        .mobile-menu { display: none; }
        @media (max-width: 767px) {
          .desktop-nav { display: none !important; }
          .hamburger-btn { display: flex !important; flex-direction: column; gap: 5px; background: none; border: none; cursor: pointer; padding: 8px; }
          .hamburger-btn span { display: block; width: 24px; height: 2px; background: #FFFFFF; border-radius: 2px; }
          .mobile-menu { display: flex !important; flex-direction: column; position: absolute; top: 64px; left: 0; right: 0; background: rgba(26,59,110,0.98); padding: 16px 24px; gap: 4px; }
          .mobile-menu a { color: rgba(255,255,255,0.85); text-decoration: none; font-size: 15px; padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.08); }
          .mobile-menu a:last-child { border-bottom: none; }
        }
      `}</style>

      {/* ── HEADER ── */}
      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? "rgba(26,59,110,0.97)" : NAVY,
        backdropFilter: scrolled ? "blur(12px)" : "none",
        transition: "all 0.3s ease",
        boxShadow: scrolled ? "0 2px 20px rgba(0,0,0,0.2)" : "none"
      }}>
        <div style={{
          maxWidth: 1140, margin: "0 auto", padding: "0 24px",
          height: 64, display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          {/* Logo */}
          <a href="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
            <SchoolIcon size={34} color="#FFFFFF"/>
            <div>
              <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 18, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.1 }}>
                Raider Depot
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Riverwood Food Security Program
              </div>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="desktop-nav" style={{ alignItems: "center", gap: 8 }}>
            <a href="#about" style={navLink}>About</a>
            <a href="#how-it-works" style={navLink}>How it works</a>
            <a href="#volunteer" style={navLink}>Volunteer</a>
            <span style={{ width: 1, height: 20, background: "rgba(255,255,255,0.2)", margin: "0 4px" }}/>
            <a href="/volunteer" style={navLinkSmall}>Volunteer portal</a>
            <a href="/volunteer/packing" style={navLinkSmall}>Pack Orders</a>
            <a href="/admin" style={navLinkSmall}>Admin portal</a>
            <a href="/login" style={navLinkSmall}>Client account</a>
            <a href="/order" style={{
              background: "#FFFFFF",
              color: NAVY,
              padding: "8px 20px",
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: "none",
              marginLeft: 8,
              transition: "all 0.2s"
            }}
            onMouseEnter={e => { e.currentTarget.style.background = LIGHT; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#FFFFFF"; }}
            >Place an order →</a>
          </nav>

          {/* Hamburger button (mobile only) */}
          <button className="hamburger-btn" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
            <span/><span/><span/>
          </button>

          {/* Mobile slide-down menu */}
          {menuOpen && (
            <div className="mobile-menu">
              <a href="#about" onClick={() => setMenuOpen(false)}>About</a>
              <a href="#how-it-works" onClick={() => setMenuOpen(false)}>How it works</a>
              <a href="#volunteer" onClick={() => setMenuOpen(false)}>Volunteer</a>
              <a href="/volunteer" onClick={() => setMenuOpen(false)}>Volunteer portal</a>
              <a href="/volunteer/packing" onClick={() => setMenuOpen(false)}>Pack Orders</a>
              <a href="/admin" onClick={() => setMenuOpen(false)}>Admin portal</a>
              <a href="/login" onClick={() => setMenuOpen(false)}>Client account</a>
              <a href="/order" onClick={() => setMenuOpen(false)}>Place an order →</a>
            </div>
          )}
        </div>
      </header>

      {/* ── HERO ── */}
      <section style={{
        background: `linear-gradient(160deg, ${NAVY} 0%, ${MID} 55%, #1E5FA8 100%)`,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "120px 24px 80px",
        position: "relative",
        overflow: "hidden"
      }}>
        {/* Background geometric decoration */}
        <div style={{
          position: "absolute", top: 0, right: 0, width: "50%", height: "100%",
          background: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
          pointerEvents: "none"
        }}/>
        <div style={{
          position: "absolute", bottom: -60, left: -60,
          width: 400, height: 400, borderRadius: "50%",
          background: "rgba(255,255,255,0.03)", pointerEvents: "none"
        }}/>
        <div style={{
          position: "absolute", top: 80, right: -80,
          width: 500, height: 500, borderRadius: "50%",
          background: "rgba(255,255,255,0.04)", pointerEvents: "none"
        }}/>

        <div style={{ maxWidth: 780, textAlign: "center", position: "relative", zIndex: 1 }}>
          {/* Icon pair */}
          <div style={{ display: "flex", justifyContent: "center", gap: 20, marginBottom: 32, animation: "float 4s ease-in-out infinite" }}>
            <SchoolIcon size={52} color="rgba(255,255,255,0.9)"/>
            <DepotIcon  size={52} color="rgba(255,255,255,0.9)"/>
          </div>

          <div style={{
            display: "inline-block", background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 20, padding: "6px 18px",
            fontSize: 12, fontWeight: 600, letterSpacing: "0.1em",
            textTransform: "uppercase", color: "rgba(255,255,255,0.85)",
            marginBottom: 24, animation: "fadeDown 0.6s ease"
          }}>
            Riverwood International Charter School
          </div>

          <h1 style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: "clamp(40px, 7vw, 72px)",
            fontWeight: 700,
            color: "#FFFFFF",
            lineHeight: 1.1,
            marginBottom: 24,
            animation: "fadeDown 0.6s ease 0.1s both"
          }}>
            The Raider Depot
          </h1>

          <p style={{
            fontSize: "clamp(17px, 2.5vw, 22px)",
            color: "rgba(255,255,255,0.8)",
            lineHeight: 1.65,
            marginBottom: 48,
            maxWidth: 580,
            margin: "0 auto 48px",
            animation: "fadeDown 0.6s ease 0.2s both"
          }}>
            Groceries and household staples for Riverwood students and their families —
            anonymous, accessible, and available every week.
          </p>

          <div style={{
            display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap",
            animation: "fadeDown 0.6s ease 0.3s both"
          }}>
            <a href="/order" style={heroCTAPrimary}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
            >
              Place an order
            </a>
            <a href="#how-it-works" style={heroCTASecondary}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
            >
              How it works ↓
            </a>
          </div>

          <p style={{ marginTop: 24, fontSize: 13, color: "rgba(255,255,255,0.45)", animation: "fadeDown 0.6s ease 0.4s both" }}>
            A simple, private way to get what you need — just your Raider Depot Token.
          </p>
        </div>

        {/* Wave divider */}
        <div style={{ position: "absolute", bottom: -1, left: 0, right: 0 }}>
          <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ display: "block", width: "100%", height: 60 }}>
            <path d="M0,40 C360,0 1080,80 1440,20 L1440,60 L0,60 Z" fill="#F7FAFD"/>
          </svg>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" style={{ background: "#F7FAFD", padding: "100px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", gap: 64, alignItems: "center", flexWrap: "wrap" }}>

            {/* Left: text */}
            <div style={{ flex: "1 1 420px" }}>
              <FadeIn>
                <div style={sectionLabel}>About the program</div>
                <h2 style={sectionHeading}>Ensuring Riverwood students have what they need to thrive</h2>
                <p style={bodyText}>
                  The Raider Depot is a student resource at Riverwood International
                  Charter School, offering groceries and everyday essentials so students
                  can stay focused on school and daily life.
                </p>
                <p style={{ ...bodyText, marginTop: 16 }}>
                  Getting started is easy and private. Students can pick up a
                  Raider Depot Token from a school counselor, use it to place
                  bi-weekly orders online, and pick up during designated hours.
                  It's a simple, confidential process designed to make access easy
                  and convenient.
                </p>
              </FadeIn>

              <FadeIn delay={0.1}>
                <div style={{ marginTop: 32, display: "flex", gap: 24, flexWrap: "wrap" }}>
                  {[
                    { n: "Bi-weekly", label: "pickup cycles" },
                    { n: "100%", label: "anonymous" },
                    { n: "Free", label: "to all students" },
                  ].map(({ n, label }) => (
                    <div key={label} style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 32, fontWeight: 700, color: NAVY }}>{n}</div>
                      <div style={{ fontSize: 13, color: "#718096", marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </FadeIn>
            </div>

            {/* Right: mission card */}
            <FadeIn delay={0.15} style={{ flex: "1 1 340px" }}>
              <div style={{
                background: `linear-gradient(145deg, ${NAVY}, ${MID})`,
                borderRadius: 20,
                padding: "40px 36px",
                color: "#FFFFFF"
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginBottom: 16 }}>
                  Our mission
                </div>
                <h3 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 24, lineHeight: 1.35, marginBottom: 20 }}>
                  We're here to support Riverwood students so they can focus on what matters most — learning.
                </h3>
                <div style={{ width: 40, height: 2, background: "rgba(255,255,255,0.3)", marginBottom: 20 }}/>
                <p style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", lineHeight: 1.7 }}>
                  The Raider Depot is a privacy-first, technology-enabled program
                  designed to support students and their families with the essentials
                  they need to thrive.
                </p>
                <div style={{ marginTop: 28, padding: "16px 20px", background: "rgba(255,255,255,0.1)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.15)" }}>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 6 }}>How to get started</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Receive your Raider Depot Token card from your school counselor. That's it.</div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" style={{ background: LIGHT, padding: "100px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn style={{ textAlign: "center", marginBottom: 60 }}>
            <div style={sectionLabel}>Three simple steps</div>
            <h2 style={{ ...sectionHeading, textAlign: "center" }}>Using the Raider Depot is easy</h2>
          </FadeIn>

          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <StepCard
              number="1" delay={0}
              icon={<TokenIcon size={44}/>}
              title="Receive your Token"
              body="Your school counselor will give you a Raider Depot Token card. Your Token is your only identifier — no name, no ID, no forms. Keep your card safe — you'll use it every time you order."
            />
            <StepCard
              number="2" delay={0.1}
              icon={
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                  <rect width="44" height="44" rx="10" fill={LIGHT}/>
                  <rect x="8" y="12" width="28" height="20" rx="3" fill={NAVY} opacity="0.15"/>
                  <rect x="8" y="12" width="28" height="8" rx="3" fill={NAVY} opacity="0.35"/>
                  <circle cx="22" cy="28" r="4" fill={MID}/>
                  <path d="M20 28 L22 30 L25 26" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
              }
              title="Place your order"
              body="Click the Place Your Order button and enter your Token number. Select the items your family needs from the catalog and submit. Takes under two minutes."
            />
            <StepCard
              number="3" delay={0.2}
              icon={<DepotIcon size={44} color={NAVY}/>}
              title="Pick up your order"
              body="Pick-up is available on Thursday with two convenient options: Carpool pick-up (2:00–3:00 PM outside Door 49) or after-school pick-up (3:30–3:45 PM at the Raider Depot next to the Senior Commons)."
            />
          </div>

          <FadeIn delay={0.3} style={{ textAlign: "center", marginTop: 52 }}>
            <a href="/order" style={{
              display: "inline-block",
              background: NAVY,
              color: "#FFFFFF",
              padding: "16px 40px",
              borderRadius: 10,
              fontSize: 16,
              fontWeight: 600,
              textDecoration: "none",
              transition: "all 0.2s"
            }}
            onMouseEnter={e => e.currentTarget.style.background = MID}
            onMouseLeave={e => e.currentTarget.style.background = NAVY}
            >
              Place an order now →
            </a>
            <p style={{ marginTop: 14, fontSize: 13, color: "#718096" }}>
              Already have an account?{" "}
              <a href="/login" style={{ color: MID, textDecoration: "none", fontWeight: 500 }}>Sign in</a>
              {" "}to view your order history.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── VOLUNTEER ── */}
      <section id="volunteer" style={{ background: "#F7FAFD", padding: "100px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <FadeIn style={{ textAlign: "center", marginBottom: 60 }}>
            <div style={sectionLabel}>Support the program</div>
            <h2 style={{ ...sectionHeading, textAlign: "center" }}>Volunteer with us</h2>
            <p style={{ ...bodyText, textAlign: "center", maxWidth: 560, margin: "0 auto", marginTop: 12 }}>
              The Raider Depot runs on community support. Here's how you can help.
            </p>
          </FadeIn>

          <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
            <DonateCard
              delay={0}
              icon={
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <circle cx="14" cy="8" r="5" fill={NAVY} opacity="0.3"/>
                  <circle cx="14" cy="8" r="3" fill={NAVY} opacity="0.6"/>
                  <path d="M4 24 C4 18 24 18 24 24" stroke={NAVY} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                </svg>
              }
              title="Volunteer your time"
              body="Volunteers help pack and distribute orders on Tuesday, Wednesday, and Thursday during the school day. No experience needed — just a willingness to help and a friendly face for our clients."
              cta="Sign up to volunteer"
              href="mailto:volunteer@riverwoodraiderdepot.org?subject=Raider%20Depot%20Volunteer%20Inquiry"
            />
          </div>
        </div>
      </section>

      {/* ── SPONSORS ── */}
      <section id="sponsors" style={{
        background: `linear-gradient(180deg, ${NAVY} 0%, #0F2447 100%)`,
        padding: "80px 24px"
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
          <FadeIn>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 32 }}>
              Our partners &amp; sponsors
            </div>
          </FadeIn>

          <FadeIn delay={0.1}>
            <div style={{ display: "flex", gap: 40, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>

              {/* Kroger sponsor tile */}
              <div style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 14,
                padding: "24px 36px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 14
              }}>
                <KrogerWordmark height={36}/>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", maxWidth: 200, textAlign: "center", lineHeight: 1.5 }}>
                  Proud grocery partner — bulk order fulfillment &amp; gift card program
                </div>
              </div>

              {/* Riverwood tile */}
              <div style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 14,
                padding: "24px 36px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 14
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <SchoolIcon size={28} color="rgba(255,255,255,0.85)"/>
                  <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 17, fontWeight: 700, color: "#FFFFFF" }}>
                    Riverwood ICS
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", maxWidth: 200, textAlign: "center", lineHeight: 1.5 }}>
                  Host school — Fulton County Schools
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p style={{ marginTop: 48, fontSize: 13, color: "rgba(255,255,255,0.35)" }}>
              Interested in supporting the Raider Depot?{" "}
              <a href="#volunteer" style={{ color: "rgba(255,255,255,0.6)", textDecoration: "none" }}>
                Volunteer with us
              </a>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: "#0F2447", padding: "48px 24px 32px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", gap: 48, flexWrap: "wrap", justifyContent: "space-between", marginBottom: 40 }}>

            <div style={{ flex: "1 1 280px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <SchoolIcon size={26} color="rgba(255,255,255,0.8)"/>
                <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 16, color: "#FFFFFF" }}>Raider Depot</div>
              </div>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.7, maxWidth: 280 }}>
                Groceries and household staples for Riverwood students and their families — anonymous, accessible, and available every week.
              </p>
              <div style={{ marginTop: 16, display: "inline-block", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "8px 14px" }}>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>Pickup options</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", fontWeight: 500 }}>Thursday: Carpool (2–3 PM) or After-school (3:30–3:45 PM)</div>
              </div>
            </div>

            <div style={{ flex: "0 0 160px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 16 }}>Program</div>
              {[
                ["About", "#about"],
                ["How it works", "#how-it-works"],
                ["Place an order", "/order"],
                ["Client account", "/login"],
              ].map(([label, href]) => (
                <a key={label} href={href} style={{ display: "block", fontSize: 14, color: "rgba(255,255,255,0.55)", textDecoration: "none", marginBottom: 10, transition: "color 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.color = "#FFFFFF"}
                  onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.55)"}
                >{label}</a>
              ))}
            </div>

            <div style={{ flex: "0 0 160px" }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: 16 }}>Support</div>
              {[
                ["Volunteer", "#volunteer"],
                ["Volunteer portal", "/volunteer"],
                ["Pack Orders", "/volunteer/packing"],
                ["Admin portal", "/admin"],
                ["Contact school office", "https://riverwood.fultonschools.org/"],
              ].map(([label, href]) => (
                <a key={label} href={href} style={{ display: "block", fontSize: 14, color: "rgba(255,255,255,0.55)", textDecoration: "none", marginBottom: 10, transition: "color 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.color = "#FFFFFF"}
                  onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.55)"}
                >{label}</a>
              ))}
            </div>
          </div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 24, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>
              © 2026 The Raider Depot · Riverwood International Charter School
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>
              No personal information is ever collected. All orders are anonymous. ·{" "}
              <a href="/privacy" style={{ color: "rgba(255,255,255,0.35)", textDecoration: "none" }}>Privacy notice</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

// ─── Shared style objects ─────────────────────────────────────────────────────
const navLink = {
  color: "rgba(255,255,255,0.75)",
  textDecoration: "none",
  fontSize: 14,
  fontWeight: 500,
  padding: "6px 12px",
  borderRadius: 6,
  transition: "all 0.15s"
};

const navLinkSmall = {
  color: "rgba(255,255,255,0.55)",
  textDecoration: "none",
  fontSize: 13,
  padding: "6px 10px",
  borderRadius: 6,
  transition: "all 0.15s"
};

const heroCTAPrimary = {
  display: "inline-block",
  background: "#FFFFFF",
  color: NAVY,
  padding: "16px 36px",
  borderRadius: 10,
  fontSize: 16,
  fontWeight: 700,
  textDecoration: "none",
  transition: "all 0.2s",
  boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
};

const heroCTASecondary = {
  display: "inline-block",
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.25)",
  color: "#FFFFFF",
  padding: "16px 32px",
  borderRadius: 10,
  fontSize: 16,
  fontWeight: 500,
  textDecoration: "none",
  transition: "all 0.2s"
};

const sectionLabel = {
  display: "inline-block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: MID,
  background: LIGHT,
  padding: "4px 12px",
  borderRadius: 20,
  marginBottom: 16
};

const sectionHeading = {
  fontFamily: "'DM Serif Display', Georgia, serif",
  fontSize: "clamp(28px, 4vw, 42px)",
  fontWeight: 700,
  color: NAVY,
  lineHeight: 1.2,
  marginBottom: 20
};

const bodyText = {
  fontSize: 16,
  color: "#4A5568",
  lineHeight: 1.75
};
